import { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import type { CompletedOnboardingRow, OnboardingCounts, OnboardingListRow } from "../../types/onboarding";

interface Loaded<T> {
  data: T | null;
  error: string | null;
}

const NOT_LOADED = { data: null, error: null };

// Each list is null until its answer arrives, and starts over when the company or the view changes, so a
// switch never shows the previous company's rows. The completed list is fetched only while it is shown.
export function useOnboardingDashboard(companyId: number | null, showCompleted: boolean) {
  const { get } = useApi();
  // Both lists are GETs to /api/onboardings, and a GET supersedes one in flight to the same path on its instance.
  const { get: getCompleted } = useApi();
  const [open, setOpen] = useState<Loaded<OnboardingListRow[]>>(NOT_LOADED);
  const [counts, setCounts] = useState<Loaded<OnboardingCounts>>(NOT_LOADED);
  const [completed, setCompleted] = useState<Loaded<CompletedOnboardingRow[]>>(NOT_LOADED);
  const forCompany = companyId === null ? "" : `companyId=${companyId}`;

  useEffect(() => {
    let current = true;
    setOpen(NOT_LOADED);
    get<OnboardingListRow[]>("/api/onboardings?status=open")
      .then((data) => { if (current) setOpen({ data, error: null }); })
      .catch((err: Error) => { if (current) setOpen({ data: null, error: err.message }); });
    return () => { current = false; };
  }, [get]);

  useEffect(() => {
    let current = true;
    setCounts(NOT_LOADED);
    get<OnboardingCounts>(`/api/onboardings/counts${forCompany && `?${forCompany}`}`)
      .then((data) => { if (current) setCounts({ data, error: null }); })
      .catch((err: Error) => { if (current) setCounts({ data: null, error: err.message }); });
    return () => { current = false; };
  }, [get, forCompany]);

  useEffect(() => {
    let current = true;
    setCompleted(NOT_LOADED);
    if (showCompleted) {
      getCompleted<CompletedOnboardingRow[]>(`/api/onboardings?status=complete${forCompany && `&${forCompany}`}`)
        .then((data) => { if (current) setCompleted({ data, error: null }); })
        .catch((err: Error) => { if (current) setCompleted({ data: null, error: err.message }); });
    }
    return () => { current = false; };
  }, [getCompleted, forCompany, showCompleted]);

  return { open, counts, completed };
}
